import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [homeAirport, setHomeAirport] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !firstName || !lastName || !homeAirport) return;

    setIsSubmitting(true);
    
    const formData = new URLSearchParams();
    formData.append('firstName', firstName);
    formData.append('lastName', lastName);
    formData.append('email', email);
    formData.append('homeAirport', homeAirport);

    try {
      await fetch('https://readdy.ai/api/form/d2u40vj3b7dn4hkpn15g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
    
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <section id="waitlist" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl p-16 border border-gray-100 shadow-xl">
            <div className="w-24 h-24 flex items-center justify-center rounded-full mx-auto mb-8 bg-[#2C2CE8]">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-[#2C2CE8] mb-6">
              You're in! Welcome to smarter travel.
            </h2>
            <p className="text-xl text-[#2C2CE8]/70 mb-8">
              You've joined an exclusive community of innovative travelers. Get ready for early access to features that will transform how you experience air travel.
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-[#2C2CE8]/80">
                <strong>What's next?</strong> We'll send you behind-the-scenes updates, beta access invitations, and travel insights as we prepare for launch.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C2CE8] mb-8">
            Experience the future of frictionless air travel
          </h2>
          <p className="text-xl text-[#2C2CE8]/70 max-w-3xl mx-auto leading-relaxed">
            Say goodbye to guesswork. Fly smarter with travel intelligence designed for the people.
          </p>
        </div>
        
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-gray-100">
          <form id="flyte-waitlist-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C2CE8] focus:border-[#2C2CE8] text-gray-900"
                  placeholder="Your first name"
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C2CE8] focus:border-[#2C2CE8] text-gray-900"
                  placeholder="Your last name"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C2CE8] focus:border-[#2C2CE8] text-gray-900"
                placeholder="hello@example.com"
              />
            </div>
            
            <div>
              <label htmlFor="homeAirport" className="block text-sm font-semibold text-gray-700 mb-2">
                Home Airport Code *
              </label>
              <input
                type="text"
                id="homeAirport"
                name="homeAirport"
                value={homeAirport}
                onChange={(e) => setHomeAirport(e.target.value.toUpperCase())}
                required
                maxLength={3}
                pattern="[A-Z]{3}"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C2CE8] focus:border-[#2C2CE8] text-gray-900 uppercase"
                placeholder="JFK"
                title="Please enter a 3-letter airport code (e.g., JFK, LAX, LHR)"
              />
              <p className="text-gray-500 text-xs mt-1">
                Enter your 3-letter airport code (e.g., JFK, LAX, LHR, CDG)
              </p>
            </div>
            
            <div className="text-center pt-4">
              <div className="flex justify-center">
                <Button
                  type="submit"
                  size="lg"
                  className="px-12 py-4 text-lg font-semibold whitespace-nowrap cursor-pointer"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-3"></i>
                      Securing Your Spot...
                    </>
                  ) : (
                    'Get Early Access'
                  )}
                </Button>
              </div>
              
              <p className="text-gray-500 text-sm mt-6">
                Your data is protected and will never be sold or shared with third parties.
              </p>
            </div>
          </form>
          
          <div className="flex justify-end mt-4">
            <p className="text-gray-400 text-xs">*2024 data</p>
          </div>
        </div>
      </div>
    </section>
  );
}
